import React, { useState } from "react";
import { CONTACTUS_API } from "../hooks/Apis";

const FileCase = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    title: "",
    description: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(CONTACTUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.title, // map title to subject
          message: formData.description, // map description to message
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          title: "",
          description: "",
        });
      } else {
        alert(data.error || "Failed to submit grievance. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting grievance:", error);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-6 flex flex-col items-center">
      <div className="max-w-4xl w-full bg-white rounded-xl shadow-xl p-8 border border-blue-100">
        {/* Official Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mr-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-blue-900">Register Your Grievance</h1>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-700 p-4 rounded-r-lg text-left mb-6">
            <p className="text-gray-800 text-sm leading-relaxed">
              If you have faced unfair trade practices, defective goods, misleading advertisements, overcharging, denial of services, or any violation of your consumer rights, you can submit your grievance through this page.
              The Consumer Rights Council (CRC) is committed to guiding consumers and helping them seek rightful resolution through awareness, assistance, and support.
            </p>
          </div>
        </div>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              Grievance Submitted Successfully!
            </h2>
            <p className="text-gray-700 max-w-md mx-auto">
              Thank you for filing your grievance. Our team will review your case and contact you within 3-5 business days.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-6 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all"
            >
              Submit Another Grievance
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="max-w-md">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
                placeholder="10-digit phone number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
              />
            </div>

            {/* Case Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Complaint Against *
                <span className="text-gray-500 font-normal ml-1">(Name of the Company/Service Provider)</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                placeholder="Enter the name of the company or service provider"
              />
            </div>

            {/* Case Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Complaint Description/Details *
                <span className="text-gray-500 font-normal ml-1">(Up to 1000 words)</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="6"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all resize-vertical"
                placeholder="Please provide detailed information about your grievance, including dates, locations, and any relevant circumstances..."
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                className="w-full py-3.5 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 transition-all shadow-md"
              >
                Submit Grievance
              </button>
              <p className="text-xs text-gray-500 text-center mt-3">
                By submitting this form, you acknowledge that the information provided is accurate to the best of your knowledge.
              </p>
            </div>
          </form>
        )}
        
       
      </div>
    </div>
  );
};

export default FileCase;